import React, { useState, useEffect } from 'react'
import { 
  ClockIcon, 
  EyeIcon, 
  TruckIcon, 
  CheckCircleIcon, 
  XCircleIcon,
  MagnifyingGlassIcon,
  CalendarIcon,
  MapPinIcon,
  UserIcon,
  DocumentTextIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ExclamationTriangleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'

interface Operacion {
  id: string
  tipo: 'envio' | 'entrega' | 'recogida' | 'mantenimiento'
  descripcion: string
  fecha: string
  estado: 'completado' | 'cancelado' | 'en_proceso' | 'pendiente'
  vehiculo?: string
  conductor?: string
  origen?: string
  destino?: string
  cliente?: string
  costo?: number
  duracion?: number
  observaciones?: string
  prioridad: 'alta' | 'media' | 'baja'
}

const PaginaHistorialOperaciones: React.FC = () => {
  const [operaciones, setOperaciones] = useState<Operacion[]>([])
  const [filtroEstado, setFiltroEstado] = useState<string>('todos')
  const [filtroTipo, setFiltroTipo] = useState<string>('todos')
  const [busqueda, setBusqueda] = useState<string>('')
  const [fechaInicio, setFechaInicio] = useState<string>('')
  const [fechaFin, setFechaFin] = useState<string>('')
  const [operacionSeleccionada, setOperacionSeleccionada] = useState<Operacion | null>(null)
  const [mostrarModal, setMostrarModal] = useState<boolean>(false)

  useEffect(() => {
    // Datos de ejemplo más completos
    const operacionesEjemplo: Operacion[] = [
      {
        id: '1',
        tipo: 'envio',
        descripcion: 'Envío de mercancía electrónica a Bogotá',
        fecha: '2024-01-15T10:30:00Z',
        estado: 'completado',
        vehiculo: 'ABC-123',
        conductor: 'Juan Pérez',
        origen: 'Medellín, Antioquia',
        destino: 'Bogotá, Cundinamarca',
        cliente: 'TechCorp S.A.S',
        costo: 450000,
        duracion: 8.5,
        prioridad: 'alta',
        observaciones: 'Entrega exitosa en horario programado'
      },
      {
        id: '2',
        tipo: 'entrega',
        descripcion: 'Entrega de productos farmacéuticos',
        fecha: '2024-01-14T14:20:00Z',
        estado: 'completado',
        vehiculo: 'DEF-456',
        conductor: 'María García',
        origen: 'Centro de distribución',
        destino: 'Farmacia San Rafael',
        cliente: 'Farmacia San Rafael',
        costo: 85000,
        duracion: 2.3,
        prioridad: 'alta',
        observaciones: 'Productos refrigerados entregados correctamente'
      },
      {
        id: '3',
        tipo: 'recogida',
        descripcion: 'Recogida de documentos legales',
        fecha: '2024-01-13T09:15:00Z',
        estado: 'cancelado',
        vehiculo: 'GHI-789',
        conductor: 'Carlos López',
        origen: 'Bufete Jurídico Asociados',
        destino: 'Juzgado Civil',
        cliente: 'Bufete Jurídico Asociados',
        costo: 35000,
        duracion: 0,
        prioridad: 'media',
        observaciones: 'Cliente canceló por cambio de fecha de audiencia'
      },
      {
        id: '4',
        tipo: 'mantenimiento',
        descripcion: 'Mantenimiento preventivo vehículo ABC-123',
        fecha: '2024-01-12T08:00:00Z',
        estado: 'completado',
        vehiculo: 'ABC-123',
        conductor: 'Taller Mecánico',
        costo: 320000,
        duracion: 4.0,
        prioridad: 'media',
        observaciones: 'Cambio de aceite, filtros y revisión general'
      },
      {
        id: '5',
        tipo: 'envio',
        descripcion: 'Transporte de maquinaria industrial',
        fecha: '2024-01-11T06:45:00Z',
        estado: 'en_proceso',
        vehiculo: 'JKL-012',
        conductor: 'Roberto Silva',
        origen: 'Zona Industrial Norte',
        destino: 'Cali, Valle del Cauca',
        cliente: 'Industrias del Valle',
        costo: 1200000,
        duracion: 12.0,
        prioridad: 'alta',
        observaciones: 'Carga especial, requiere escolta'
      },
      {
        id: '6',
        tipo: 'entrega',
        descripcion: 'Distribución de alimentos perecederos',
        fecha: '2024-01-10T05:30:00Z',
        estado: 'pendiente',
        vehiculo: 'MNO-345',
        conductor: 'Ana Martínez',
        origen: 'Central de Abastos',
        destino: 'Supermercado El Ahorro',
        cliente: 'Supermercado El Ahorro',
        costo: 180000,
        duracion: 3.5,
        prioridad: 'alta',
        observaciones: 'Programado para mañana temprano'
      }
    ]
    setOperaciones(operacionesEjemplo)
  }, [])

  const operacionesFiltradas = operaciones.filter(operacion => {
    const cumpleFiltroEstado = filtroEstado === 'todos' || operacion.estado === filtroEstado
    const cumpleFiltroTipo = filtroTipo === 'todos' || operacion.tipo === filtroTipo
    
    const cumpleBusqueda = busqueda === '' || 
      operacion.descripcion.toLowerCase().includes(busqueda.toLowerCase()) ||
      operacion.vehiculo?.toLowerCase().includes(busqueda.toLowerCase()) ||
      operacion.conductor?.toLowerCase().includes(busqueda.toLowerCase()) ||
      operacion.cliente?.toLowerCase().includes(busqueda.toLowerCase())
    
    const fechaOperacion = new Date(operacion.fecha)
    const cumpleFechaInicio = fechaInicio === '' || fechaOperacion >= new Date(fechaInicio)
    const cumpleFechaFin = fechaFin === '' || fechaOperacion <= new Date(fechaFin + 'T23:59:59')
    
    return cumpleFiltroEstado && cumpleFiltroTipo && cumpleBusqueda && cumpleFechaInicio && cumpleFechaFin
  })

  const getEstadoColor = (estado: string) => {
    switch (estado) {
      case 'completado':
        return 'text-green-600 bg-green-100'
      case 'cancelado':
        return 'text-red-600 bg-red-100'
      case 'en_proceso':
        return 'text-blue-600 bg-blue-100'
      case 'pendiente':
        return 'text-yellow-600 bg-yellow-100'
      default:
        return 'text-gray-600 bg-gray-100'
    }
  }

  const getEstadoIcon = (estado: string) => {
    switch (estado) {
      case 'completado':
        return <CheckCircleIcon className="h-4 w-4" />
      case 'cancelado':
        return <XCircleIcon className="h-4 w-4" />
      case 'en_proceso':
        return <ClockIcon className="h-4 w-4" />
      case 'pendiente':
        return <ExclamationTriangleIcon className="h-4 w-4" />
      default:
        return <ClockIcon className="h-4 w-4" />
    }
  }

  const getTipoIcon = (tipo: string) => {
    switch (tipo) {
      case 'envio':
        return <TruckIcon className="h-5 w-5" />
      case 'entrega':
        return <CheckCircleIcon className="h-5 w-5" />
      case 'recogida':
        return <MapPinIcon className="h-5 w-5" />
      case 'mantenimiento':
        return <DocumentTextIcon className="h-5 w-5" />
      default:
        return <TruckIcon className="h-5 w-5" />
    }
  }

  const getPrioridadColor = (prioridad: string) => {
    switch (prioridad) {
      case 'alta':
        return 'text-red-700 bg-red-50 border-red-200'
      case 'media':
        return 'text-yellow-700 bg-yellow-50 border-yellow-200'
      case 'baja':
        return 'text-green-700 bg-green-50 border-green-200'
      default:
        return 'text-gray-700 bg-gray-50 border-gray-200'
    }
  }

  const formatearMoneda = (valor: number) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(valor)
  }

  const abrirModal = (operacion: Operacion) => {
    setOperacionSeleccionada(operacion)
    setMostrarModal(true)
  }

  const cerrarModal = () => {
    setOperacionSeleccionada(null)
    setMostrarModal(false)
  }

  // Calcular métricas
  const totalOperaciones = operaciones.length
  const operacionesCompletadas = operaciones.filter(op => op.estado === 'completado').length
  const operacionesEnProceso = operaciones.filter(op => op.estado === 'en_proceso').length
  const ingresosTotales = operaciones
    .filter(op => op.estado === 'completado' && op.costo)
    .reduce((total, op) => total + (op.costo || 0), 0)

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Historial de Operaciones</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">Consulta y gestiona el historial completo de todas las operaciones realizadas</p>
      </div>

      {/* Métricas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <ChartBarIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Operaciones</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{totalOperaciones}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
              <CheckCircleIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Completadas</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{operacionesCompletadas}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
              <ClockIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">En Proceso</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{operacionesEnProceso}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
              <ArrowTrendingUpIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Ingresos Totales</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatearMoneda(ingresosTotales)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtros y búsqueda */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Filtros y Búsqueda</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Búsqueda */}
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Buscar
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar por descripción, vehículo, conductor o cliente..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Estado */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Estado
            </label>
            <select
              value={filtroEstado}
              onChange={(e) => setFiltroEstado(e.target.value)}
              className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="todos">Todos</option>
              <option value="completado">Completado</option>
              <option value="en_proceso">En proceso</option>
              <option value="pendiente">Pendiente</option>
              <option value="cancelado">Cancelado</option>
            </select>
          </div>

          {/* Tipo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Tipo
            </label>
            <select
              value={filtroTipo}
              onChange={(e) => setFiltroTipo(e.target.value)}
              className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="todos">Todos</option>
              <option value="envio">Envío</option>
              <option value="entrega">Entrega</option>
              <option value="recogida">Recogida</option>
              <option value="mantenimiento">Mantenimiento</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          {/* Fecha inicio */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Fecha inicio
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <CalendarIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="date"
                value={fechaInicio}
                onChange={(e) => setFechaInicio(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Fecha fin */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Fecha fin
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <CalendarIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="date"
                value={fechaFin}
                onChange={(e) => setFechaFin(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Lista de operaciones */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Operaciones ({operacionesFiltradas.length})
            </h3>
          </div>

          <div className="space-y-4">
            {operacionesFiltradas.map((operacion) => (
              <div
                key={operacion.id}
                className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 hover:shadow-md transition-all duration-200"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1">
                    <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                      {getTipoIcon(operacion.tipo)}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-2">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white truncate">
                          {operacion.descripcion}
                        </h3>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getPrioridadColor(operacion.prioridad)}`}>
                          {operacion.prioridad.toUpperCase()}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm text-gray-600 dark:text-gray-400">
                        <div className="flex items-center space-x-2">
                          <CalendarIcon className="h-4 w-4" />
                          <span>
                            {new Date(operacion.fecha).toLocaleDateString('es-ES', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                        
                        {operacion.vehiculo && (
                          <div className="flex items-center space-x-2">
                            <TruckIcon className="h-4 w-4" />
                            <span>{operacion.vehiculo}</span>
                          </div>
                        )}
                        
                        {operacion.conductor && (
                          <div className="flex items-center space-x-2">
                            <UserIcon className="h-4 w-4" />
                            <span>{operacion.conductor}</span>
                          </div>
                        )}
                        
                        {operacion.cliente && (
                          <div className="flex items-center space-x-2">
                            <UserIcon className="h-4 w-4" />
                            <span>Cliente: {operacion.cliente}</span>
                          </div>
                        )}
                        
                        {operacion.origen && operacion.destino && (
                          <div className="flex items-center space-x-2 md:col-span-2">
                            <MapPinIcon className="h-4 w-4" />
                            <span>{operacion.origen} → {operacion.destino}</span>
                          </div>
                        )}
                        
                        {operacion.costo && (
                          <div className="flex items-center space-x-2">
                            <span className="font-medium text-green-600 dark:text-green-400">
                              {formatearMoneda(operacion.costo)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3 ml-4">
                    <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getEstadoColor(operacion.estado)}`}>
                      {getEstadoIcon(operacion.estado)}
                      <span className="ml-2 capitalize">{operacion.estado.replace('_', ' ')}</span>
                    </div>
                    
                    <button
                      onClick={() => abrirModal(operacion)}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <EyeIcon className="h-4 w-4 mr-1" />
                      Ver detalles
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {operacionesFiltradas.length === 0 && (
            <div className="text-center py-12">
              <div className="mx-auto h-24 w-24 text-gray-400">
                <ClockIcon className="h-full w-full" />
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">No hay operaciones</h3>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                No se encontraron operaciones con los filtros seleccionados.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Modal de detalles de operación */}
      {mostrarModal && operacionSeleccionada && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-lg">
                  {getTipoIcon(operacionSeleccionada.tipo)}
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {operacionSeleccionada.descripcion}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    ID: {operacionSeleccionada.id}
                  </p>
                </div>
              </div>
              <button
                onClick={cerrarModal}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Información básica */}
              <div className="space-y-4">
                <h4 className="text-lg font-medium text-gray-900 dark:text-white">Información General</h4>
                
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <CalendarIcon className="h-5 w-5 text-gray-400" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">Fecha:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {new Date(operacionSeleccionada.fecha).toLocaleDateString('es-ES', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>

                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Estado:</span>
                    <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getEstadoColor(operacionSeleccionada.estado)}`}>
                      {getEstadoIcon(operacionSeleccionada.estado)}
                      <span className="ml-1 capitalize">{operacionSeleccionada.estado.replace('_', ' ')}</span>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Prioridad:</span>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getPrioridadColor(operacionSeleccionada.prioridad)}`}>
                      {operacionSeleccionada.prioridad.toUpperCase()}
                    </span>
                  </div>

                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Tipo:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                      {operacionSeleccionada.tipo}
                    </span>
                  </div>

                  {operacionSeleccionada.duracion && (
                    <div className="flex items-center space-x-2">
                      <ClockIcon className="h-5 w-5 text-gray-400" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">Duración:</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {operacionSeleccionada.duracion}
                      </span>
                    </div>
                  )}

                  {operacionSeleccionada.costo && (
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Costo:</span>
                      <span className="text-lg font-semibold text-green-600 dark:text-green-400">
                        {formatearMoneda(operacionSeleccionada.costo)}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Información de transporte */}
              <div className="space-y-4">
                <h4 className="text-lg font-medium text-gray-900 dark:text-white">Detalles de Transporte</h4>
                
                <div className="space-y-3">
                  {operacionSeleccionada.vehiculo && (
                    <div className="flex items-center space-x-2">
                      <TruckIcon className="h-5 w-5 text-gray-400" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">Vehículo:</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {operacionSeleccionada.vehiculo}
                      </span>
                    </div>
                  )}

                  {operacionSeleccionada.conductor && (
                    <div className="flex items-center space-x-2">
                      <UserIcon className="h-5 w-5 text-gray-400" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">Conductor:</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {operacionSeleccionada.conductor}
                      </span>
                    </div>
                  )}

                  {operacionSeleccionada.cliente && (
                    <div className="flex items-center space-x-2">
                      <UserIcon className="h-5 w-5 text-gray-400" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">Cliente:</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {operacionSeleccionada.cliente}
                      </span>
                    </div>
                  )}

                  {operacionSeleccionada.origen && (
                    <div className="flex items-start space-x-2">
                      <MapPinIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                      <div>
                        <span className="text-sm text-gray-600 dark:text-gray-400">Origen:</span>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {operacionSeleccionada.origen}
                        </p>
                      </div>
                    </div>
                  )}

                  {operacionSeleccionada.destino && (
                    <div className="flex items-start space-x-2">
                      <MapPinIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                      <div>
                        <span className="text-sm text-gray-600 dark:text-gray-400">Destino:</span>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {operacionSeleccionada.destino}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Observaciones */}
            {operacionSeleccionada.observaciones && (
              <div className="mt-6">
                <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-3">Observaciones</h4>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    {operacionSeleccionada.observaciones}
                  </p>
                </div>
              </div>
            )}

            {/* Botones de acción */}
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={cerrarModal}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PaginaHistorialOperaciones